import React, { useEffect, useRef } from 'react';

import { bind } from 'bind-event-listener';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { LoadingSkeleton } from '../../../../common/ui/loading-skeleton';

import { ErrorSection } from './error-section';

interface BodyProps {
	children?: React.ReactNode;
	errorDescription?: React.ReactNode;
	errorHeader?: string;
	hasError?: boolean;
	isFooterShown: boolean;
	isLoading?: boolean;
	onBodyScrolled: (props: { showBottomShadow: boolean; showTopShadow: boolean }) => void;
}

const paddingStylesWithFooter = xcss({
	height: '100%',
	paddingRight: 'space.400',
	paddingLeft: 'space.400',
});

const paddingStylesWithoutFooter = xcss({
	height: '100%',
	paddingBottom: 'space.400',
	paddingRight: 'space.400',
	paddingLeft: 'space.400',
});

const errorBoxStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
});

const defaultStyles = xcss({
	overflowY: 'auto',
	flexGrow: 1,
});

export const Body = (props: BodyProps): React.JSX.Element => {
	const {
		isLoading = false,
		hasError = false,
		errorHeader,
		errorDescription,
		isFooterShown,
		children,
		onBodyScrolled,
	} = props;

	const ref = useRef<null | HTMLElement>(null);

	useEffect(() => {
		const div = ref.current;

		if (!div) {
			return;
		}

		const handleScroll = (): void => {
			const showTopShadow = div.scrollTop > 0;
			const showBottomShadow = div.scrollHeight - div.scrollTop > div.clientHeight;

			onBodyScrolled({ showTopShadow, showBottomShadow });
		};

		// On mount, set the shadow if content is scrollable
		handleScroll();

		// @TODO may need to debounce this
		return bind(div, {
			type: 'scroll',
			listener: handleScroll,
		});
	}, [onBodyScrolled]);

	if (hasError) {
		return (
			<Box testId="sidebar-main-error" xcss={[paddingStylesWithoutFooter, errorBoxStyles]}>
				<ErrorSection header={errorHeader} description={errorDescription} />
			</Box>
		);
	}

	if (isLoading) {
		return (
			<Box testId="sidebar-main-loading" xcss={[paddingStylesWithoutFooter, defaultStyles]}>
				<LoadingSkeleton />
			</Box>
		);
	}

	return (
		<Box
			testId="sidebar-main"
			ref={ref}
			tabIndex={0} // Makes the container focusable
			xcss={[isFooterShown ? paddingStylesWithFooter : paddingStylesWithoutFooter, defaultStyles]}
		>
			{children}
		</Box>
	);
};
